/**
 * jadualService.js — simpanan JADUAL TikTok ke Firestore (Langkah 1)
 *
 * PATH:
 *   users/{uid}/jadual/_shared                       → { templates[], akaun[], migratedAccounts[] }
 *   users/{uid}/jadual/{akaunId}/hari/{YYYY-MM-DD}   → { templateId, waktuTambahan[], slot{} }
 *   users/{uid}/jadual/_backup_{ts}                  → salinan mentah localStorage (jaring migrasi)
 *
 * ⭐ TARIKH SEBENAR DARI HUJUNG KE HUJUNG (2026-09-16).
 *   Dahulu fail ini menukar offset hari ⇄ tarikh sebenar di sempadan
 *   simpanan, sementara UI kalendar bekerja dalam OFFSET (0 = hari ini,
 *   1 = esok…). Penukaran itu SENDIRI yang merosakkan data: offset dalam
 *   ingatan dikira sekali semasa halaman dibuka dan tidak pernah dikira
 *   semula, jadi halaman yang terbuka melepasi tengah malam menyimpan
 *   "esok" sebagai satu hari LEBIH LEWAT daripada yang pelanggan pilih —
 *   seluruh kalendar ditulis semula ke depan dan rekod asal dipadam.
 *   (Lihat seksyen TARIKH SEBENAR dalam scheduleV39.js.)
 *
 *   Sekarang UI kalendar sendiri berkunci 'YYYY-MM-DD', jadi fail ini
 *   TIDAK menukar apa-apa lagi — ia hanya menyusun semula bentuk data
 *   (ingatan rata ⇄ satu dokumen setiap tarikh). Tiada baseline, tiada
 *   apa yang boleh bergeser.
 *
 * ⚠️ Waktu (minit) ialah nombor. Kunci slot dalam ingatan = `${offset}_${minit}`.
 *    Di server, minit jadi kunci medan dalam `slot` (cth "510").
 *
 * Zon waktu: SEMUA pengiraan guna waktu tempatan mesin pelanggan
 * (keputusan user 8.4) — roda TikTok menetapkan waktu ikut jam browser,
 * jadi memaksa satu zon tetap akan merosakkan pelanggan di zon lain.
 */

import { db } from '../firebase';
import {
    doc, collection, getDoc, getDocs, setDoc, writeBatch, runTransaction,
} from 'firebase/firestore';

/* ⛔ `offsetKeTarikh` dan `tarikhKeOffset` DIBUANG 2026-09-16 — jangan
   pasang semula. Kedua-duanya mengira "hari ini" pada saat ia dipanggil,
   jadi mana-mana nilai yang dibawa dari sebelum tengah malam ditukar
   kepada tarikh yang SALAH, senyap. Kalendar kini berkunci tarikh sebenar
   dari skrin sampai ke server; tiada lagi tempat yang perlu menukar.
   Penukaran draf LAMA (berkunci offset) berlaku sekali sahaja semasa draf
   itu dibaca dari storan browser — lihat `naikTarafSusunanTarikh` dalam
   components/scheduleV39.js. */

const rujukShared = (uid) => doc(db, 'users', uid, 'jadual', '_shared');
const rujukHariColl = (uid, akaunId) => collection(db, 'users', uid, 'jadual', akaunId, 'hari');
const rujukHari = (uid, akaunId, tarikh) => doc(db, 'users', uid, 'jadual', akaunId, 'hari', tarikh);

/* ---------- Penukar: satu akaun (ingatan) → dokumen per-tarikh ---------
   Ingatan: { hariTemplate:{tarikh:tid}, isian:{`${tarikh}_${minit}`:{pid,s,…}}, waktuTambahan:{tarikh:[minit]} }
   Server : { [tarikh]: { templateId, waktuTambahan:[minit], slot:{minit:{…}} } }
   Kunci hari kedua-duanya TARIKH SEBENAR 'YYYY-MM-DD' — tiada penukaran.
   Hanya tarikh yang ADA isi (template / slot / waktu tambahan) dipulangkan.
   ⛔ Kunci yang bukan tarikh (draf lama berkunci offset) DILANGKAU, bukan
      diteka: menekanya bermakna menulis kerja pelanggan ke tarikh yang
      dia tidak pernah pilih — itulah pepijat yang kerja ini membaikinya.
      Penukaran draf lama berlaku sekali sahaja semasa dibaca dari storan
      (naikTarafSusunanTarikh dalam scheduleV39.js). */
const SAH_TARIKH = /^\d{4}-\d{2}-\d{2}$/;

export function akaunKeTarikh(susunanAkaun = {}) {
    const { hariTemplate = {}, isian = {}, waktuTambahan = {} } = susunanAkaun || {};
    const perTarikh = {};

    const pastikan = (tarikh) => {
        if (!perTarikh[tarikh]) perTarikh[tarikh] = { templateId: null, waktuTambahan: [], slot: {} };
        return perTarikh[tarikh];
    };

    for (const tarikh in hariTemplate) {
        if (SAH_TARIKH.test(tarikh) && hariTemplate[tarikh]) pastikan(tarikh).templateId = hariTemplate[tarikh];
    }
    for (const tarikh in waktuTambahan) {
        const arr = waktuTambahan[tarikh];
        if (SAH_TARIKH.test(tarikh) && Array.isArray(arr) && arr.length) pastikan(tarikh).waktuTambahan = [...arr];
    }
    for (const k in isian) {
        const e = isian[k];
        if (!e) continue;
        const pisah = String(k).split('_');
        const minit = pisah.pop();
        const tarikh = pisah.join('_');
        if (!SAH_TARIKH.test(tarikh)) continue;
        /* Simpan RUJUKAN sahaja (pid + set), bukan salinan penuh objek produk —
           kalendar & senarai produk hidup dalam skrin sama, jadi nama sudah ada
           dalam ingatan.
           ⚠️ KEMBAR: medan slot disenarai SATU PERSATU di sini DAN dalam isian
           paparan + pindahBanyak (JadualTiktokV39.jsx). Medan baharu → tambah
           di kedua-dua (3 Nov: `md` tercicir di paparan → Post now = "Scheduled"). */
        const bersih = { pid: e.pid || e.p?.id, s: e.s };
        /* ★ LANGKAH 5 — status per-slot: st = status post ('complete'|'draft'|
           'failed'|'uncertain'|'running'), sb = sebab. Disimpan bersama rujukan
           supaya "sudah dijadualkan / draf / gagal" KEKAL merentas muat semula
           dan antara browser.
           'running' TURUT disimpan (sengaja): larian TikTok boleh mengambil
           beberapa minit, jadi kalau pengguna muat semula di tengah jalan, slot
           kekal 'running' dan NAIK TARAF kepada complete/draft/failed sebaik
           produk membawa hasil terminal (effect graduasi berjalan semula pada
           muat). Larian yang benar-benar terputus kekal 'running' → jalur
           "N sedang dihantar · Stop" muncul supaya pengguna boleh bersihkan. */
        if (e.st) { bersih.st = e.st; if (e.sb) bersih.sb = e.sb; }
        /* ★ [2026-09-16] pf = platform larian slot ('fb'; tiada = TikTok). Tanpa
           baris ini penanda hilang pada muat semula → effect graduasi membaca
           kunci kad yang salah (tiktok_ untuk larian Facebook). */
        if (e.pf === 'fb') bersih.pf = 'fb';
        if (e.pf === 'sp') bersih.pf = 'sp'; // telefon: Retry + graduasi mesti tahu ini bukan TikTok
        /* ★ [2026-09-27] md = cara post larian ('now'). Tanpa baris ini slot Post now
           kembali berlabel "Scheduled" selepas muat semula. */
        if (e.md === 'now') bersih.md = 'now';
        if (!bersih.pid || !bersih.s) continue;
        pastikan(tarikh).slot[minit] = bersih;
    }
    return perTarikh;
}

/* ---------- Penukar: dokumen per-tarikh (server) → satu akaun (ingatan) --- */
export function tarikhKeAkaun(dokTarikh = {}) {
    const hariTemplate = {};
    const isian = {};
    const waktuTambahan = {};

    for (const tarikh in dokTarikh) {
        if (!SAH_TARIKH.test(tarikh)) continue;
        const d = dokTarikh[tarikh] || {};
        if (d.templateId) hariTemplate[tarikh] = d.templateId;
        if (Array.isArray(d.waktuTambahan) && d.waktuTambahan.length) {
            waktuTambahan[tarikh] = [...d.waktuTambahan];
        }
        const slot = d.slot || {};
        for (const minit in slot) {
            const e = slot[minit] || {};
            if (!e.pid || !e.s) continue;
            isian[`${tarikh}_${minit}`] = { ...e };
        }
    }
    return { hariTemplate, isian, waktuTambahan };
}

/* ---------- MUAT dari server ------------------------------------------
   Pulang { ada, templates, akaun, susunan } di mana `susunan` sudah dalam
   bentuk ingatan (offset). `ada:false` bermakna doc _shared belum wujud
   (akaun ini belum pernah naik server → pemanggil patut migrasi). */
export async function muatDariServer(uid) {
    const snapShared = await getDoc(rujukShared(uid));
    if (!snapShared.exists()) return { ada: false };

    const data = snapShared.data() || {};
    const templates = Array.isArray(data.templates) ? data.templates : [];
    const akaun = Array.isArray(data.akaun) && data.akaun.length ? data.akaun : [];
    const migratedAccounts = Array.isArray(data.migratedAccounts) ? data.migratedAccounts : [];

    const susunan = {};
    await Promise.all(akaun.map(async (a) => {
        try {
            const snap = await getDocs(rujukHariColl(uid, a.id));
            const dokTarikh = {};
            snap.forEach((d) => { dokTarikh[d.id] = d.data(); });
            susunan[a.id] = tarikhKeAkaun(dokTarikh);
        } catch {
            susunan[a.id] = { hariTemplate: {}, isian: {}, waktuTambahan: {} };
        }
    }));

    return { ada: true, templates, akaun, susunan, migratedAccounts };
}

/* ---------- MUAT susunan SATU akaun (v3.9.2 — fix multi-browser) -------
   Kalendar dimuat SEKALI pada mount, jadi dua browser yang membuka kalendar
   akaun yang sama tidak pernah nampak perubahan satu sama lain. Fungsi ini
   dipakai di dua tempat dalam WorkspaceView:
   (1) Pagar anti-double-post sebelum Run kalendar — status slot TERKINI
       dibaca dari server supaya slot yang sudah dijalankan browser LAIN
       dilangkau, bukan di-post dua kali.
   (2) Muat semula setiap kali pengguna kembali ke tab Schedule.
   Pulangkan bentuk ingatan {hariTemplate, isian, waktuTambahan}, atau null
   bila gagal — pemanggil fail-open (teruskan dengan data sedia ada). */
export async function muatSusunanAkaun(uid, akaunId) {
    if (!uid || !akaunId) return null;
    try {
        const snap = await getDocs(rujukHariColl(uid, akaunId));
        const dokTarikh = {};
        snap.forEach((d) => { dokTarikh[d.id] = d.data(); });
        return tarikhKeAkaun(dokTarikh);
    } catch { return null; }
}

/* ---------- MIGRASI localStorage → server (PER AKAUN, union) -----------
   ⛔ PER AKAUN, bukan sekali untuk semua: setiap browser pelanggan pegang
      senarai akaun BERLAINAN. Kalau ditanda "sudah dipindah" sekali untuk
      semua, browser yang naik dulu menang dan akaun yang hanya wujud di
      browser lain HILANG selamanya.
   Peraturan:
     - Akaun yang sudah ada di server (atau dalam migratedAccounts) → JANGAN sentuh.
     - Akaun baru → cipta + tulis dokumen tarikhnya + tambah ke migratedAccounts.
     - Templates & senarai akaun → union (jangan ganti).
     - Salinan mentah localStorage disimpan sebagai jaring.
   Pulang bentuk sama seperti muatDariServer (selepas migrasi). */
export async function migrasiKeServer(uid, dataLokal, mentahJson) {
    const localAkaun = Array.isArray(dataLokal?.akaun) ? dataLokal.akaun : [];
    const localTemplates = Array.isArray(dataLokal?.templates) ? dataLokal.templates : [];
    const localSusunan = (dataLokal?.susunan && typeof dataLokal.susunan === 'object') ? dataLokal.susunan : {};

    const stamp = Date.now();

    /* 1) Gabung _shared secara atomik supaya dua browser yang migrasi
          serentak tidak saling memadam senarai akaun. */
    let migratedAccounts = [];
    let akaunGabung = [];
    let templatesGabung = [];
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(rujukShared(uid));
        const ada = snap.exists() ? (snap.data() || {}) : {};
        const serverAkaun = Array.isArray(ada.akaun) ? ada.akaun : [];
        const serverTemplates = Array.isArray(ada.templates) ? ada.templates : [];
        migratedAccounts = Array.isArray(ada.migratedAccounts) ? [...ada.migratedAccounts] : [];

        const petaAkaun = new Map(serverAkaun.map((a) => [a.id, a]));
        for (const a of localAkaun) if (a?.id && !petaAkaun.has(a.id)) petaAkaun.set(a.id, a);
        akaunGabung = [...petaAkaun.values()];

        const petaTpl = new Map(serverTemplates.map((t) => [t.id, t]));
        for (const t of localTemplates) if (t?.id && !petaTpl.has(t.id)) petaTpl.set(t.id, t);
        templatesGabung = [...petaTpl.values()];

        tx.set(rujukShared(uid), {
            akaun: akaunGabung,
            templates: templatesGabung,
            migratedAccounts,
            lastMigrateAt: stamp,
        }, { merge: true });
    });

    /* 2) Simpan salinan mentah (jaring pemulihan) — sekali per migrasi. */
    if (mentahJson) {
        try {
            await setDoc(doc(db, 'users', uid, 'jadual', `_backup_${stamp}`), {
                mentah: String(mentahJson).slice(0, 900000), // bawah had 1MB
                pada: stamp,
            });
        } catch { /* backup gagal ≠ gagalkan migrasi */ }
    }

    /* 3) Tulis dokumen tarikh untuk akaun yang BELUM dimigrasi sahaja. */
    const barus = [];
    for (const a of localAkaun) {
        if (!a?.id) continue;
        if (migratedAccounts.includes(a.id)) continue;
        const perTarikh = akaunKeTarikh(localSusunan[a.id] || {});
        await tulisSemuaTarikh(uid, a.id, perTarikh);
        barus.push(a.id);
    }

    /* 4) Tandakan akaun yang baru dimigrasi (union). */
    if (barus.length) {
        await setDoc(rujukShared(uid), {
            migratedAccounts: [...new Set([...migratedAccounts, ...barus])],
        }, { merge: true });
    }

    return muatDariServer(uid);
}

/* Tulis satu set dokumen tarikh (dipakai masa migrasi + sebagai sandaran). */
async function tulisSemuaTarikh(uid, akaunId, perTarikh) {
    const tarikhs = Object.keys(perTarikh);
    if (!tarikhs.length) return;
    /* writeBatch: had 500 operasi/batch. Satu akaun tak mungkin cecah 30
       tarikh, jadi satu batch memadai — tapi kita pecah demi selamat. */
    for (let i = 0; i < tarikhs.length; i += 400) {
        const batch = writeBatch(db);
        for (const tarikh of tarikhs.slice(i, i + 400)) {
            batch.set(rujukHari(uid, akaunId, tarikh), perTarikh[tarikh]);
        }
        await batch.commit();
    }
}

/* ---------- SIMPAN 'shared' (templates + akaun) — nyahlantun ----------- */
const timerShared = {};
export function simpanShared(uid, { templates, akaun }, ms = 800) {
    if (!uid) return;
    clearTimeout(timerShared[uid]);
    timerShared[uid] = setTimeout(() => {
        setDoc(rujukShared(uid), {
            templates: Array.isArray(templates) ? templates : [],
            akaun: Array.isArray(akaun) ? akaun : [],
        }, { merge: true }).catch(() => { /* draf — jangan ganggu kerja */ });
    }, ms);
}

/* ---------- SIMPAN susunan satu akaun — nyahlantun + diff per tarikh ----
   Hanya tarikh yang BERUBAH ditulis; tarikh yang jadi kosong dipadam.
   `refDisimpan` = objek yang dipegang pemanggil { [uid_akaun]: {tarikh:json} }
   supaya kita boleh banding dengan tulisan terakhir dan elak menulis semula
   seluruh kalendar setiap kali satu kad diseret. */
const timerAkaun = {};
export function simpanSusunanAkaun(uid, akaunId, susunanAkaun, refDisimpan, ms = 800) {
    if (!uid || !akaunId) return;
    const kunci = `${uid}__${akaunId}`;
    clearTimeout(timerAkaun[kunci]);
    timerAkaun[kunci] = setTimeout(async () => {
        try {
            const perTarikh = akaunKeTarikh(susunanAkaun || {});
            const lama = refDisimpan.current[kunci] || {};
            const baru = {};
            const batch = writeBatch(db);
            let ops = 0;

            for (const tarikh in perTarikh) {
                const json = JSON.stringify(perTarikh[tarikh]);
                baru[tarikh] = json;
                if (lama[tarikh] !== json) {
                    batch.set(rujukHari(uid, akaunId, tarikh), perTarikh[tarikh]);
                    ops++;
                }
            }
            for (const tarikh in lama) {
                if (!(tarikh in perTarikh)) {
                    batch.delete(rujukHari(uid, akaunId, tarikh));
                    ops++;
                }
            }
            if (ops > 0) await batch.commit();
            refDisimpan.current[kunci] = baru;
        } catch { /* draf — jangan ganggu kerja */ }
    }, ms);
}

/* Paksa tulis segera (dipakai bila tab bertukar / halaman ditutup / sebelum Run). */
export function flushSimpanan(uid, akaunId, susunanAkaun, refDisimpan) {
    const kunci = `${uid}__${akaunId}`;
    clearTimeout(timerAkaun[kunci]);
    const perTarikh = akaunKeTarikh(susunanAkaun || {});
    const lama = refDisimpan.current?.[kunci] || {};
    const baru = {};
    const batch = writeBatch(db);
    let ops = 0;
    for (const tarikh in perTarikh) {
        const json = JSON.stringify(perTarikh[tarikh]);
        baru[tarikh] = json;
        if (lama[tarikh] !== json) { batch.set(rujukHari(uid, akaunId, tarikh), perTarikh[tarikh]); ops++; }
    }
    for (const tarikh in lama) if (!(tarikh in perTarikh)) { batch.delete(rujukHari(uid, akaunId, tarikh)); ops++; }
    if (refDisimpan.current) refDisimpan.current[kunci] = baru;
    return ops > 0 ? batch.commit().catch(() => {}) : Promise.resolve();
}

/* Padam seluruh kalendar satu akaun (bila akaun dipadam pengguna). */
export async function padamAkaun(uid, akaunId) {
    try {
        const snap = await getDocs(rujukHariColl(uid, akaunId));
        const batch = writeBatch(db);
        let ops = 0;
        snap.forEach((d) => { batch.delete(d.ref); ops++; });
        if (ops) await batch.commit();
    } catch { /* biar — dokumen induk _shared yang jadi sumber senarai */ }
}
